import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, IconButton, TextField, Button, CircularProgress, Tooltip, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Copiloto agentico, Fase A (2026-10-24): reescritura completa sobre
// el copiloto global de la Fase A inicial (un solo hilo continuo) --
// ahora con conversaciones reales, contexto de pagina, streaming por
// socket.io, tool-calling (6 herramientas de SOLO LECTURA) y tarjetas
// clicables. Nunca ofrece herramientas de escritura ni envia
// WhatsApp real (eso queda para Fases B/C, ver
// docs/pendientes-de-decision.md).

const MAX_TEXTAREA_ROWS = 6;

// "Cerebro del copiloto", Bloque 1 (2026-10-24): comandos "/" del router
// sin IA (CopilotRouter.ts, backend) -- deben coincidir exactamente con
// los `slashCommand` de ahi.
const SLASH_COMMANDS = [
  { command: "/espera", hint: "Clientes esperando respuesta" },
  { command: "/citas", hint: "Huecos libres hoy en Citas" },
  { command: "/metricas", hint: "Métricas del día" },
  { command: "/dia", hint: "Resumen de Tu día" },
  { command: "/tareas", hint: "Tareas de hoy" },
  { command: "/buscar", hint: "Buscar un contacto -- escribe el nombre después" },
  { command: "/ayuda", hint: "Qué puede hacer el copiloto" },
];

// Sugerencias dinamicas por pagina -- punto 9 del plan. Rutas no
// listadas caen al set generico.
const SUGGESTIONS_BY_ROUTE = (ctx) => {
  if (ctx.ticketId) {
    return [
      "Resume esta conversación",
      "¿Qué le respondo?",
      "¿Hay algo pendiente de este cliente?",
    ];
  }
  if (ctx.route.startsWith("/tickets")) {
    return [
      "¿Qué clientes esperan respuesta?",
      "Resume mis pendientes de hoy",
      "¿Cuántas conversaciones abiertas hay?",
    ];
  }
  if (ctx.route.startsWith("/contacts")) {
    return [
      "Busca un contacto por nombre o teléfono",
      "¿Cuántos contactos hay en total?",
    ];
  }
  if (ctx.route.toLowerCase().startsWith("/kanban")) {
    return [
      "¿Qué tickets llevan más tiempo en el tablero?",
      "Resume mis pendientes de hoy",
    ];
  }
  if (ctx.route.startsWith("/tu-dia")) {
    return [
      "Resume las señales de hoy",
      "¿Hay huecos libres en Citas hoy?",
    ];
  }
  return [
    "Resume mis pendientes de hoy",
    "¿Qué clientes esperan respuesta?",
    "¿Hay huecos libres en Citas hoy?",
  ];
};

const TOOL_CARD_ICON = {
  "buscar_tickets": <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} />,
  "buscar_contactos": <PersonOutlineIcon sx={{ fontSize: 16 }} />,
  "clientes_esperando_respuesta": <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} />,
  "huecos_libres_citas": <ScheduleOutlinedIcon sx={{ fontSize: 16 }} />,
};

// Tarjeta clicable -- punto 10/11. Cada herramienta devuelve una lista
// de "items" navegables en su outputSchema (ver backend); si no hay
// items reconocibles, no se renderiza nada especial (el texto del
// modelo ya describe el resultado). Calidad conversacional, Bloque
// 1.5: maximo 3 tarjetas (antes 8) -- menos ruido, el texto del
// modelo ya dice lo importante.
const MAX_CARDS = 3;
function ToolResultCards({ toolId, output }) {
  const items = Array.isArray(output?.items) ? output.items : [];
  if (items.length === 0) return null;
  const extra = items.length - MAX_CARDS;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: "6px" }}>
      {items.slice(0, MAX_CARDS).map((item, i) => (
        <Box
          key={item.to || i}
          component={item.to ? RouterLink : "div"}
          to={item.to}
          sx={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "8px 10px", borderRadius: "10px",
            border: "1px solid rgba(167,139,250,0.35)",
            background: "rgba(167,139,250,0.08)",
            textDecoration: "none", color: "inherit", cursor: item.to ? "pointer" : "default",
            "&:hover": item.to ? { background: "rgba(167,139,250,0.16)" } : {},
          }}
        >
          {TOOL_CARD_ICON[toolId] || <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.title}
            </Typography>
            {item.subtitle && (
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
      {extra > 0 && (
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", px: "2px" }}>hay {extra} más</Typography>
      )}
    </Box>
  );
}

// Calidad conversacional, Bloque 1.5 (punto 10): las llamadas a
// herramientas de un mismo turno se agrupan en una sola linea
// discreta ("Consultó N fuentes ▸") en vez de una burbuja por cada
// una -- se expande para ver las tarjetas de cada herramienta.
// Bloque Integraciones + Copiloto Fase B: tarjeta Aprobar/Editar/Cancelar
// -- SIEMPRE visible (nunca dentro del grupo colapsable de arriba),
// unica forma de que una herramienta de escritura propuesta por el
// copiloto se ejecute de verdad. "Editar" es deliberadamente un textarea
// con el JSON del input (en vez de un formulario por herramienta): con
// 19+ herramientas distintas registradas, un formulario a medida por
// cada una es mucho mas codigo para el mismo resultado -- el usuario ve
// exactamente los campos que la herramienta va a recibir y puede
// corregir cualquiera antes de aprobar.
function ApprovalCard({ message, resolved, resolving, onApprove, onReject }) {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState(() => JSON.stringify(message.toolOutput?.input || {}, null, 2));
  const [editError, setEditError] = useState(null);
  const toolName = message.toolOutput?.toolName || message.toolId;

  const handleApproveClick = () => {
    if (!editing) {
      onApprove(null);
      return;
    }
    try {
      const parsed = JSON.parse(editedText);
      setEditError(null);
      onApprove(parsed);
    } catch (e) {
      setEditError("JSON inválido -- revisa las comillas y comas antes de aprobar.");
    }
  };

  return (
    <Box sx={{
      alignSelf: "flex-start", maxWidth: "95%", width: "100%",
      borderRadius: "12px", border: "1px solid rgba(251,191,36,0.4)",
      background: "rgba(251,191,36,0.08)", padding: "10px 12px",
      display: "flex", flexDirection: "column", gap: "8px",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 12.5, fontWeight: 700, color: "#B45309" }}>
        <AutoAwesomeIcon sx={{ fontSize: 15 }} />
        Pendiente de aprobación: {toolName}
      </Box>
      {editing ? (
        <>
          <TextField
            multiline minRows={3} maxRows={10} size="small"
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            sx={{ fontFamily: "monospace", "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
          />
          {editError && <Typography sx={{ fontSize: 11.5, color: "error.main" }}>{editError}</Typography>}
        </>
      ) : (
        <Box component="pre" sx={{
          margin: 0, fontSize: 11.5, fontFamily: "monospace", whiteSpace: "pre-wrap",
          color: theme.palette.text.secondary, maxHeight: 140, overflowY: "auto",
        }}>
          {JSON.stringify(message.toolOutput?.input || {}, null, 2)}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
        <Button size="small" variant="contained" color="warning" disabled={resolved || resolving} onClick={handleApproveClick}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: "999px" }}>
          {editing ? "Aprobar con cambios" : "Aprobar"}
        </Button>
        <Button size="small" disabled={resolved || resolving} onClick={() => setEditing((v) => !v)}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: "999px" }}>
          {editing ? "Cancelar edición" : "Editar"}
        </Button>
        <Button size="small" color="inherit" disabled={resolved || resolving} onClick={onReject}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: "999px" }}>
          Cancelar
        </Button>
      </Box>
      {resolved && (
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Ya se resolvió esta propuesta.</Typography>
      )}
    </Box>
  );
}

function ToolCallGroup({ tools }) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  if (tools.length === 0) return null;
  const label = tools.length === 1 ? "Consultó 1 fuente" : `Consultó ${tools.length} fuentes`;
  return (
    <Box sx={{ alignSelf: "flex-start", maxWidth: "92%" }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", gap: "2px", cursor: "pointer",
          fontSize: 11.5, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
          padding: "2px 0",
        }}
      >
        {open ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ChevronRightIcon sx={{ fontSize: 14 }} />}
        {label}
      </Box>
      {open && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {tools.map((t, i) => <ToolResultCards key={i} toolId={t.toolId} output={t.toolOutput} />)}
        </Box>
      )}
    </Box>
  );
}

const CopilotPanel = ({ onClose, fullscreen, onToggleFullscreen }) => {
  const theme = useTheme();
  const location = useLocation();
  const params = useParams();
  const { user, socket } = useContext(AuthContext);

  const pageContext = useMemo(() => ({
    route: location.pathname,
    ticketId: params.ticketId ? Number(params.ticketId) : null,
  }), [location.pathname, params.ticketId]);

  const [contextDismissed, setContextDismissed] = useState(false);
  const [contextLabel, setContextLabel] = useState(null);

  useEffect(() => {
    setContextDismissed(false);
    if (pageContext.ticketId) {
      api.get(`/tickets/${pageContext.ticketId}`)
        .then(({ data }) => setContextLabel(`Ticket de ${data?.contact?.name || "contacto"}`))
        .catch(() => setContextLabel(`Ticket #${pageContext.ticketId}`));
    } else {
      setContextLabel(null);
    }
  }, [pageContext.ticketId]);

  const [conversations, setConversations] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [toolStatus, setToolStatus] = useState(null);
  const streamingRef = useRef("");
  const [streamingText, setStreamingText] = useState("");
  const requestIdRef = useRef(null);
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get("/ai-agent/copilot/conversations");
      setConversations(data.conversations);
      return data.conversations;
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const openConversation = async (id) => {
    setConversationId(id);
    setHistoryOpen(false);
    if (!id) {
      setMessages([]);
      return;
    }
    try {
      const { data } = await api.get(`/ai-agent/copilot/conversations/${id}/messages`);
      setMessages(data.messages);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    fetchConversations().then((list) => {
      if (list.length > 0) openConversation(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages, streamingText, toolStatus]);

  // Streaming por socket.io -- punto 4 del plan. Cada evento trae el
  // mismo requestId que esta llamada genero, asi nunca se mezcla con
  // una pregunta vieja si el usuario manda dos seguidas o cambia de
  // pestaña rapido.
  useEffect(() => {
    if (!socket) return;
    const onToken = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      streamingRef.current += data.token;
      setStreamingText(streamingRef.current);
    };
    const onToolStart = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      setToolStatus(data.label || "Trabajando…");
    };
    const onToolEnd = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      setToolStatus(null);
    };
    const onDone = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      setMessages((old) => [...old, ...data.messages]);
      setStreamingText("");
      streamingRef.current = "";
      setToolStatus(null);
      setSending(false);
      requestIdRef.current = null;
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
        fetchConversations();
      }
    };
    const onError = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      toastError({ response: { data: { error: data.error || "El copiloto no pudo responder." } } });
      setStreamingText("");
      streamingRef.current = "";
      setToolStatus(null);
      setSending(false);
      requestIdRef.current = null;
    };
    socket.on("copilot:token", onToken);
    socket.on("copilot:tool_start", onToolStart);
    socket.on("copilot:tool_end", onToolEnd);
    socket.on("copilot:done", onDone);
    socket.on("copilot:error", onError);
    return () => {
      socket.off("copilot:token", onToken);
      socket.off("copilot:tool_start", onToolStart);
      socket.off("copilot:tool_end", onToolEnd);
      socket.off("copilot:done", onDone);
      socket.off("copilot:error", onError);
    };
  }, [socket, conversationId]);

  const handleSend = async (messageOverride) => {
    const message = (messageOverride ?? input).trim();
    if (!message || sending) return;
    setSending(true);
    setInput("");
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    requestIdRef.current = requestId;
    streamingRef.current = "";
    setStreamingText("");
    setMessages((old) => [...old, { role: "user", content: message, createdAt: new Date().toISOString() }]);
    try {
      await api.post("/ai-agent/copilot/conversations/chat", {
        requestId,
        conversationId,
        message,
        context: contextDismissed ? {} : pageContext,
      });
    } catch (err) {
      toastError(err);
      setSending(false);
      requestIdRef.current = null;
    }
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showGreeting = messages.length === 0 && !streamingText;

  // Calidad conversacional, Bloque 1.5 (punto 10): agrupa filas
  // role:"tool" consecutivas en un solo bloque colapsable, en vez de
  // una burbuja de chat por cada herramienta llamada. Bloque
  // Integraciones + Copiloto Fase B: una fila "tool" con
  // __pending_approval===true NUNCA entra a ese grupo colapsado --
  // necesita quedar siempre visible y con botones, no escondida tras
  // un "Consultó N fuentes ▸".
  const blocks = useMemo(() => {
    const result = [];
    let pendingTools = [];
    const flush = () => {
      if (pendingTools.length > 0) {
        result.push({ type: "toolGroup", tools: pendingTools });
        pendingTools = [];
      }
    };
    messages.forEach((m) => {
      if (m.role === "tool" && m.toolOutput?.__pending_approval === true) {
        flush();
        result.push({ type: "approval", m });
      } else if (m.role === "tool") {
        pendingTools.push(m);
      } else {
        flush();
        result.push({ type: "message", m });
      }
    });
    flush();
    return result;
  }, [messages]);

  const [resolvedApprovalIds, setResolvedApprovalIds] = useState(() => new Set());
  const [resolvingApprovalId, setResolvingApprovalId] = useState(null);

  const handleResolveApproval = async (message, approve, editedInput) => {
    if (!conversationId || resolvingApprovalId) return;
    setResolvingApprovalId(message.id);
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    requestIdRef.current = requestId;
    setSending(true);
    try {
      await api.post(`/ai-agent/copilot/conversations/${conversationId}/resolve-approval`, {
        requestId, approve, editedInput: editedInput || undefined,
      });
      setResolvedApprovalIds((old) => new Set(old).add(message.id));
    } catch (err) {
      toastError(err);
      setSending(false);
      requestIdRef.current = null;
    } finally {
      setResolvingApprovalId(null);
    }
  };

  // Punto 12: botones de accion debajo de la respuesta, cuando el
  // turno dejo claro de que ticket se hablaba (lastTicketId viaja
  // solo en el ultimo mensaje del turno, ver GlobalCopilotAgentService.ts).
  const handleUseAsReply = (text) => {
    window.dispatchEvent(new CustomEvent("insertQuickMessage", { detail: { quickMessage: { message: text } } }));
  };

  return (
    <Box sx={{
      flex: fullscreen ? "1 1 auto" : "0 0 360px",
      width: fullscreen ? "100%" : 360,
      borderLeft: fullscreen ? "none" : `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      background: theme.palette.mode === "dark" ? "rgba(15,21,36,0.98)" : "#faf9ff",
      display: "flex", flexDirection: "column", padding: "18px", gap: "12px", overflow: "hidden",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, fontSize: 16, color: "#A78BFA" }}>
          <AutoAwesomeIcon sx={{ fontSize: 20 }} />
          Copiloto
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
          <Tooltip title="Nueva conversación">
            <IconButton size="small" onClick={() => openConversation(null)}><AddIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Historial">
            <IconButton size="small" onClick={() => setHistoryOpen((v) => !v)}><HistoryIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
            <IconButton size="small" onClick={onToggleFullscreen}>
              {fullscreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" aria-label="Cerrar copiloto" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>

      {historyOpen && (
        <Box sx={{ border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, borderRadius: "10px", maxHeight: 180, overflowY: "auto" }}>
          {(conversations || []).length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", p: "10px" }}>Sin conversaciones todavía.</Typography>
          )}
          {(conversations || []).map((c) => (
            <Box
              key={c.id}
              onClick={() => openConversation(c.id)}
              sx={{
                padding: "8px 10px", cursor: "pointer", fontSize: 13,
                background: c.id === conversationId ? "rgba(167,139,250,0.12)" : "transparent",
                "&:hover": { background: "rgba(167,139,250,0.08)" },
              }}
            >
              {c.title || "Conversación sin título"}
            </Box>
          ))}
        </Box>
      )}

      {contextLabel && !contextDismissed && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "6px", fontSize: 12,
          padding: "6px 10px", borderRadius: "999px",
          background: "rgba(3,153,255,0.10)", color: "#0399FF", alignSelf: "flex-start",
        }}>
          <ArrowBackIcon sx={{ fontSize: 13, transform: "rotate(225deg)" }} />
          Viendo: {contextLabel}
          <IconButton size="small" onClick={() => setContextDismissed(true)} sx={{ p: "2px", color: "inherit" }}>
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", minHeight: 0, overflowY: "auto" }}>
        {showGreeting && (
          <>
            <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
              Pregunta sobre tus conversaciones, clientes o tu día. Disponible en cualquier página. <RouterLink to="/tu-dia" style={{ color: "#0399FF" }}>Ver Tu día</RouterLink>.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {SUGGESTIONS_BY_ROUTE(pageContext).map((s) => (
                <Button
                  key={s}
                  onClick={() => handleSend(s)}
                  disabled={sending}
                  sx={{
                    textAlign: "left", justifyContent: "flex-start", textTransform: "none",
                    padding: "12px 14px", borderRadius: "12px",
                    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                    color: theme.palette.text.primary, fontSize: 13,
                  }}
                >
                  {s}
                </Button>
              ))}
            </Box>
          </>
        )}

        {blocks.map((b, i) => {
          if (b.type === "toolGroup") {
            return <ToolCallGroup key={`tg-${i}`} tools={b.tools} />;
          }
          if (b.type === "approval") {
            return (
              <ApprovalCard
                key={`ap-${b.m.id}`}
                message={b.m}
                resolved={resolvedApprovalIds.has(b.m.id)}
                resolving={resolvingApprovalId === b.m.id}
                onApprove={(editedInput) => handleResolveApproval(b.m, true, editedInput)}
                onReject={() => handleResolveApproval(b.m, false, null)}
              />
            );
          }
          const m = b.m;
          const hasTicket = typeof m.lastTicketId === "number";
          return (
            <Box key={`m-${i}`} sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "92%" }}>
              {m.respondedVia === "router" && (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px",
                  fontSize: 10.5, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
                }}>
                  <span role="img" aria-label="Instantánea">⚡</span> Instantánea
                </Box>
              )}
              <Box sx={{
                padding: "8px 12px", borderRadius: "12px", fontSize: 13, whiteSpace: "pre-wrap",
                background: m.role === "user" ? "rgba(3,153,255,0.14)" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                color: theme.palette.text.primary,
              }}>
                {m.content}
              </Box>
              {m.role === "assistant" && hasTicket && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "6px" }}>
                  <Button size="small" component={RouterLink} to={`/tickets/${m.lastTicketId}`}
                    sx={{ fontSize: 11.5, textTransform: "none", borderRadius: "999px", padding: "3px 10px", minWidth: 0, border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`, color: theme.palette.text.primary }}>
                    Abrir ticket
                  </Button>
                  <Button size="small" onClick={() => handleSend(`Sugiéreme una respuesta para el ticket #${m.lastTicketId}, basada en el último mensaje real del cliente.`)}
                    sx={{ fontSize: 11.5, textTransform: "none", borderRadius: "999px", padding: "3px 10px", minWidth: 0, border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`, color: theme.palette.text.primary }}>
                    Sugerir respuesta
                  </Button>
                  <Button size="small" onClick={() => handleUseAsReply(m.content)}
                    sx={{ fontSize: 11.5, textTransform: "none", borderRadius: "999px", padding: "3px 10px", minWidth: 0, border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`, color: theme.palette.text.primary }}>
                    Usar como respuesta
                  </Button>
                  <Button size="small" onClick={() => handleSend("/citas")}
                    sx={{ fontSize: 11.5, textTransform: "none", borderRadius: "999px", padding: "3px 10px", minWidth: 0, border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`, color: theme.palette.text.primary }}>
                    Ver huecos libres
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}

        {streamingText && (
          <Box sx={{ alignSelf: "flex-start", maxWidth: "92%", padding: "8px 12px", borderRadius: "12px", fontSize: 13, whiteSpace: "pre-wrap", background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
            {streamingText}
          </Box>
        )}

        {toolStatus && (
          <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "6px", fontSize: 12, color: "text.secondary" }}>
            <CircularProgress size={12} />
            {toolStatus}
          </Box>
        )}

        {sending && !streamingText && !toolStatus && (
          <Box sx={{ alignSelf: "flex-start" }}><CircularProgress size={16} /></Box>
        )}
        <div ref={endRef} />
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {input.startsWith("/") && !input.includes(" ") && (
          (() => {
            const matches = SLASH_COMMANDS.filter((c) => c.command.startsWith(input.toLowerCase()));
            if (matches.length === 0) return null;
            return (
              <Box sx={{
                borderRadius: "10px", overflow: "hidden",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
              }}>
                {matches.map((c) => (
                  <Box
                    key={c.command}
                    onClick={() => { setInput(`${c.command} `); textareaRef.current?.focus(); }}
                    sx={{
                      display: "flex", justifyContent: "space-between", gap: "10px",
                      padding: "6px 10px", fontSize: 12.5, cursor: "pointer",
                      "&:hover": { background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{c.command}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{c.hint}</Typography>
                  </Box>
                ))}
              </Box>
            );
          })()
        )}
        <TextField
          inputRef={textareaRef}
          multiline
          maxRows={MAX_TEXTAREA_ROWS}
          placeholder="Pregunta o pide una acción… (Enter envía, Shift+Enter salto de línea, / para comandos)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleTextareaKeyDown}
          disabled={sending}
          size="small"
        />
        <Button
          variant="contained"
          onClick={() => handleSend()}
          disabled={sending || !input.trim()}
          sx={{ height: 40, borderRadius: "12px", textTransform: "none", fontWeight: 700, alignSelf: "flex-end", px: 3 }}
        >
          Enviar
        </Button>
      </Box>
    </Box>
  );
};

export default CopilotPanel;
